import { useState } from "react";
import { Box, IconButton, List, ListItemButton, ListItemText, Popover, Typography } from "@mui/material";
import { OpenInNew, WarningAmber, EventBusy, ErrorOutline } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { ReservedSlotData, BookingSlot } from "./types";
import { SlotsPopoverInformation, toPopoverBooking } from "./shared/SlotsPopoverInformation";
import { PSY_BOOKING_PURPLE, WARNING_RED } from "./constants";

type ReservedSlotCellProps = {
    reservation?: ReservedSlotData;
    bookings: Array<BookingSlot>;
};

// A booking-only PSY slot shows a client session if there is one; otherwise it's a non-client
// "busy" block (internal meeting / other service) surfaced only as a clickable "!".
const pickRepresentativeBooking = (bookings: Array<BookingSlot>) => {
    const clientBooking = bookings.find((booking) => booking.isClientSession);
    const representative = clientBooking ?? bookings[0];
    return representative;
};

const ReservationContent = ({ reservation }: { reservation: ReservedSlotData }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleWarningClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 0.5,
                    border: reservation.isWeekly ? "none" : "1px dashed #9e9e9e",
                    px: 0.5,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                    <Typography
                        variant="caption"
                        fontWeight="bold"
                        sx={{ fontSize: "10px", lineHeight: 1.2, color: reservation.isWeekly ? "inherit" : "#666" }}
                    >
                        {reservation.clientId}
                    </Typography>
                    <IconButton
                        component={Link}
                        to={`/treatments/${reservation.treatmentId}/treatment_prescription`}
                        target="_blank"
                        size="small"
                        color="secondary"
                        sx={{ p: 0 }}
                    >
                        <OpenInNew sx={{ fontSize: 12 }} />
                    </IconButton>
                </Box>
                {reservation.missedTwoConsecutiveSessions && (
                    <IconButton onClick={handleWarningClick} sx={{ p: 0 }} size="small">
                        <WarningAmber sx={{ fontSize: 16, color: WARNING_RED }} />
                    </IconButton>
                )}
            </Box>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Box sx={{ p: 1.5 }}>
                    <Typography variant="body2" fontWeight="bold" color="error" sx={{ mb: 1 }}>
                        Faltaram duas sessões consecutivas
                    </Typography>
                    <List dense sx={{ py: 0 }}>
                        <ListItemButton
                            component={Link}
                            to={`/treatments/${reservation.treatmentId}/sessions`}
                            target="_blank"
                            sx={{ py: 0.5 }}
                        >
                            <ListItemText
                                primary={`${reservation.clientId} - ${reservation.clientName}`}
                                primaryTypographyProps={{ variant: "body2" }}
                            />
                            <OpenInNew sx={{ fontSize: 14, ml: 1, color: "text.secondary" }} />
                        </ListItemButton>
                    </List>
                </Box>
            </Popover>
        </>
    );
};

const BookingOnlyContent = ({ bookings }: { bookings: Array<BookingSlot> }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const representative = pickRepresentativeBooking(bookings);
    if (!representative) return null;

    // Client session with no reservation (the gap): show the client + link to the treatment/sessions.
    if (representative.isClientSession) {
        const label = representative.clientId || representative.clientName || "Sessão";
        return (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.25, px: 0.5 }} title="Sessão agendada sem reserva">
                <EventBusy sx={{ fontSize: 16, color: PSY_BOOKING_PURPLE}} />
                <Typography variant="caption" fontWeight="bold" sx={{ fontSize: "10px", lineHeight: 1.2, color: PSY_BOOKING_PURPLE}}>
                    {label}
                </Typography>
                {representative.treatmentId > 0 && (
                    <IconButton
                        component={Link}
                        to={`/treatments/${representative.treatmentId}/sessions`}
                        target="_blank"
                        size="small"
                        sx={{ p: 0, color: PSY_BOOKING_PURPLE}}
                    >
                        <OpenInNew sx={{ fontSize: 12 }} />
                    </IconButton>
                )}
            </Box>
        );
    }

    // Non-client "busy" block (meeting / other service): just a "!" — click to see the service name or title.
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    return (
        <>
            <Box
                onClick={handleClick}
                title="Ver o que está agendado"
                sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%", cursor: "pointer" }}
            >
                <ErrorOutline sx={{ fontSize: 17, color: PSY_BOOKING_PURPLE}} />
            </Box>
            <SlotsPopoverInformation
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                reservations={[]}
                bookings={bookings.map(toPopoverBooking)}
            />
        </>
    );
};

export const ReservedSlotCell = ({ reservation, bookings }: ReservedSlotCellProps) => {
    if (reservation) {
        return <ReservationContent reservation={reservation} />;
    }

    return <BookingOnlyContent bookings={bookings} />;
};
